import { DataService } from "fg-library";
import { Column, FieldGroup, ListField, ModelField } from "./definitions";
import { firstValueFrom } from "rxjs";

export function extractModelFields(data: any[]): ModelField[] {
  return data.map((item) => ({
    tag: item.tag,
    name: item.name,
    type: item.type,
    displayName: item.displayName,
    minLength: item.minLength,
    maxLength: item.maxLength,
    isMandatory: item.isMandatory,
    isLink: item.isLink,
    isMeasurable: item.isMesurable ?? item.isMeasurable, // supports typo or variation
    isDimensional: item.isDimensional,
    fieldType: item.fieldType,
    fieldTypeKey: item.fieldTypeKey,
    translationValueField: item.translationValueField,
    translationDisplayField: item.translationDisplayField,
  }));
}

export function groupModelFieldsByFieldGroup(
  modelFields: ModelField[],
  fieldGroups: FieldGroup[]
): { [groupCode: string]: ModelField[] } {
  const fieldMap = new Map(modelFields.map((f) => [f.name, f]));

  const groupedResult: { [groupCode: string]: ModelField[] } = {};

  fieldGroups
    .filter(
      (group) => group.type === "Form" || group.type.toLowerCase() === "list"
    )
    .forEach((group) => {
      const groupModels: ModelField[] = [];

      group.groupFields.forEach((field) => {
        const match = fieldMap.get(field.propertyCode);
        if (match) {
          groupModels.push(match);
        }
      });

      if (groupModels.length) {
        groupedResult[group.code] = groupModels;
      }
    });

  return groupedResult;
}

export function generateListColumns(modelListFields: ModelField[]): {
  columns: Column[];
} {
  return {
    columns: modelListFields.map((field) => ({
      name: field.name, // fixed name as per your mapping
      type: field.type,
      displayName: field.displayName,
      enableLink: false,
      asBadge: (field.name?.toLowerCase().includes('status') || field.name?.toLowerCase().includes('version' ))? true : false,
      firstLetterIconEnabled: field.name?.toLowerCase().includes('name') ? true : false,
      dateFormat: field.type == 'date' ? {"dateFormat": "dd-MMM-yyyy"} : undefined,
      isPrefixIcon: field.type == 'date' ? true : false,
      prefixIcon:field.type == 'date' ?  "fa fa-calendar" : ""
    })),
  };
}



export async function getModelMetaData(
  dataService: DataService,
  code: any
): Promise<any> {
  var filterData = { code: code };

  const response = await firstValueFrom(
    dataService.getCustomServiceData("fgEntityTypeDetail", filterData)
  );

  //console.log(response);
  const result = JSON.parse(response.masterTemplate);
  return result;
}

export async function getAllLinkModels(
  dataService: DataService,
  links: any[]
): Promise<Record<string, any>> {
  const linkModels: Record<string, any> = {};

  await Promise.all(
    links.map(async (item) => {
      const result = await getModelMetaData(dataService, item.conf.subTypeCode);
      const allFields = extractModelFields(result.properties);
      
      // Filter out fields with tag 'System'
      const filteredFields = allFields.filter(field => field.tag !== 'System');

      linkModels[item.link] = filteredFields;
    })
  );

  //console.log("All link models fetched:", linkModels);
  return linkModels;
}


export async function fetchAllLinkModelMetaData(dataService: any, links: any[]): Promise<any> {
  const results: Record<string, any> = {};

  await Promise.all(
    links.map(async (item) => {
      const result = await getModelMetaData(dataService, item.conf.subTypeCode);
      results[item.link] = result;
    })
  );

  return results;
}

// given a field group group name returns all the fields, that includes list and form groups
export function getFieldsByGroup(daataModel: any): any {
  var modelField = extractModelFields(daataModel.properties);
  var fieldGroups = daataModel.propertyGroups;
  //console.log(modelField);
  const groupedFields = groupModelFieldsByFieldGroup(modelField, fieldGroups);

  const formFields = groupedFields.Form;
  const listFields = groupedFields.list
    ? groupedFields.list
    : groupedFields.List;

  return { listFields: listFields, formFields: formFields };
}
// arrange and return link field model data
export function extractLinks(model: any): any[] {
  if (!model || !Array.isArray(model.links)) return [];
  const linkFields = model.links.map(link => ({
    link: link.name,
    conf: {
      cardinality: link.cardinality,
      displayName: link.displayName,
      subTypeCode: link.targetEntityCode
    }
  }));
  return linkFields;
}
// get model code and conf from link field name
export function getConfByLink(linksArray: any[], linkName: string): any | undefined {
  const match = linksArray.find(linkObj => linkObj.link === linkName);
  return match?.conf;
}